import { useState, useEffect, useCallback } from "react";
import { Box, Link, ListItemText, Tooltip } from "@mui/material";
import { parse } from "papaparse";
import { useMobileLayout } from "@verity/components";
import {
	CoreModal,
	CoreButton,
	CoreTable,
	CoreTableColumn,
	CoreMenuButton,
	CoreMenuItem,
	CoreTableRow,
	CoreTextField,
	CoreIconButton,
	coreToast,
	CoreDividerWithText,
	CoreUpload,
} from "@verity/components/core";
import i18n from "../../../../i18n";
import styles from "./styles.module.css";
import CheckMarkIcon from "./check-mark.svg?react";
import CancelIcon from "./cancel-icon.svg?react";
import RedAsteriskIcon from "./red-asterisk.svg?react";
import { Trans } from "react-i18next";

type RowData = Record<string, string>;
type PartialCoreTableRow = Pick<CoreTableRow<RowData>, "data" | "index" | "absoluteIndex">;
type CustomParseError = { row: number; message: string; type: string; code: string };

function CsvParsingErrorModal({
	errors,
	onClose,
}: {
	errors: { row?: number; message: string }[];
	onClose: () => void;
}) {
	return (
		<CoreModal
			title={i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.formatError")}
			open
			onClose={onClose}
			buttons={
				<Box>
					<CoreButton color="secondary" style={{ marginRight: "5px" }} onClick={onClose}>
						{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.close")}
					</CoreButton>
				</Box>
			}
		>
			<Box sx={{ mt: 2 }}>
				{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.resolveError")}
				<br />
				{errors.map(({ row, message }) => {
					if (typeof row === "number")
						return (
							<>
								{`${i18n.t(
									"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.row",
								)}${row + 2}: ${message}`}
								<br key={row} />
							</>
						);
				})}
			</Box>
		</CoreModal>
	);
}

function CSVParseConfirmationModal({
	csvFileToBeParsedOnConfirm,
	onClose,
	onConfirm,
}: {
	csvFileToBeParsedOnConfirm: File;
	onClose: () => void;
	onConfirm: (file: File) => void;
}) {
	return (
		<CoreModal
			title={i18n.t(
				"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.csvParseConfirmTitle",
			)}
			open
			onClose={onClose}
			buttons={
				<Box>
					<CoreButton
						color="secondary"
						style={{ marginRight: "10px" }}
						onClick={() => {
							onClose();
						}}
					>
						{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.standaloneQuizOptionsTSX.cancel")}
					</CoreButton>
					<CoreButton
						color="primary"
						onClick={() => {
							onConfirm(csvFileToBeParsedOnConfirm);
							onClose();
						}}
					>
						{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.proceed")}
					</CoreButton>
				</Box>
			}
			widthFixed
			minWidth="min(720px, 100%)"
		>
			<div style={{ color: "#6B6B6B", fontSize: 15, marginTop: "4px", marginBottom: "25px" }}>
				<Trans
					i18nKey="portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.csvParseConfirmText"
					values={{ fileName: csvFileToBeParsedOnConfirm.name }}
				/>
			</div>
		</CoreModal>
	);
}

export default function InviteeInfoTable({
	columns,
	mandatoryColumns,
	rows,
	onColumnsChange,
	onRowsChange,
	shouldShowAddTestTakerTable,
	setShouldShowAddTestTakerTable,
	isCSVParsed,
	setCSVParsed,
}: {
	columns: string[];
	idColumn: string;
	mandatoryColumns: string[];
	rows: RowData[];
	rowsPerPage: number;
	onColumnsChange: (column: string[]) => void;
	onRowsChange: (rows: RowData[]) => void;
	shouldShowAddTestTakerTable: boolean;
	setShouldShowAddTestTakerTable: (value: boolean) => void;
	isCSVParsed: boolean;
	setCSVParsed: (value: boolean) => void;
}) {
	const [csvParsingErrors, setCsvParsingErrors] = useState<{ row?: number; message: string }[] | null>(null);
	const [csvFileToBeParsedOnConfirm, setCSVFileToBeParsedOnConfirm] = useState<File>();
	const isMobileLayout = useMobileLayout();

	function isRowData(row: unknown): row is RowData {
		if (typeof row !== "object" || row === null) {
			return false;
		}

		for (const key in row) {
			if (typeof key !== "string") {
				return false;
			}
		}

		return true;
	}

	useEffect(() => {
		if (rows.length === 0 && shouldShowAddTestTakerTable) {
			const newRow = { Email: "", "First Name": "", "Last Name": "" };
			const updatedRows = rows.concat([newRow]);
			onRowsChange(updatedRows);
			setCurrentlyEditedRow({
				data: newRow,
				index: 0,
				absoluteIndex: 0,
			});
		}
	}, [shouldShowAddTestTakerTable]);

	const emailRegex = /^[\w-]+(\.[\w-]+)*(\+[\w-]+)?@([\w-]+\.)+[a-zA-Z]{2,7}$/;
	const isManualTableEmpty =
		!isCSVParsed && (rows.length === 0 || mandatoryColumns.every((column) => rows.every((row) => row[column] === "")));

	const parseCSVFile = (file: File) => {
		setCurrentlyEditedRow(null);
		parse(file, {
			header: true,
			skipEmptyLines: true,
			complete({ data, meta, errors }) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const fields = meta.fields!;
				const missingHeaders = mandatoryColumns.filter((column) => !fields.includes(column));
				if (missingHeaders.length) {
					errors.push({
						row: 0,
						message: `${i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.missHeader")}${missingHeaders.join(", ")}`,
						type: "FieldMismatch",
						code: "TooFewFields",
					});
				}

				const emailFormatErrors: CustomParseError[] = [];

				data.forEach((row, index) => {
					if (isRowData(row)) {
						const email = row["Email"];
						if (!email || email.trim() === "") {
							errors.push({
								row: index,
								message: "Missing Email",
								type: "FieldMismatch",
								code: "TooFewFields",
							});
						} else if (!emailRegex.test(email)) {
							emailFormatErrors.push({
								row: index,
								message: `Invalid Email Format: ${email}`,
								type: "FieldMismatch",
								code: "InvalidEmailFormat",
							});
						}
					}
				});

				const customErrors = [...errors, ...emailFormatErrors];

				if (customErrors.length) {
					setCsvParsingErrors(customErrors);
					return;
				}

				setCSVParsed(true);
				onColumnsChange(fields);
				onRowsChange(data as RowData[]);
				coreToast.dismiss();
				coreToast.success(
					i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.csvUploadedToast"),
					{ autoClose: 2500 },
				);
			},
		});
	};

	// currentlyEditedRow is set when the user clicks on the edit button for a row.
	const [currentlyEditedRow, setCurrentlyEditedRow] = useState<PartialCoreTableRow | null>(null);

	const handleFileUpload = useCallback(
		(file: File) => {
			if (!file) return;

			if (!file.name.toLowerCase().endsWith(".csv")) {
				coreToast.dismiss();
				coreToast.error(
					i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.fileTypeErrorToast"),
					{ autoClose: 2500 },
				);
				return;
			}

			if (
				!isManualTableEmpty ||
				(currentlyEditedRow && mandatoryColumns.some((column) => currentlyEditedRow.data[column] !== ""))
			) {
				setCSVFileToBeParsedOnConfirm(file);
			} else {
				parseCSVFile(file);
			}
		},
		[isManualTableEmpty, mandatoryColumns, currentlyEditedRow],
	);

	const isRowCurrentlyEdited = (row: CoreTableRow<RowData>) => {
		return row.absoluteIndex === currentlyEditedRow?.absoluteIndex;
	};

	const tableColumns: CoreTableColumn<RowData>[] = columns.map((column, index) => ({
		key: column,
		header: (
			<>
				{column}{" "}
				{isMobileLayout && currentlyEditedRow && index === 0 && (
					<Tooltip
						title={i18n.t(
							"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.fieldRequired",
						)}
						componentsProps={{
							tooltip: {
								sx: {
									fontSize: "14px",
								},
							},
						}}
					>
						<span>
							<RedAsteriskIcon />
						</span>
					</Tooltip>
				)}
			</>
		),
		tableCellContentWidth: "100%",
		width: "28%",
		minWidth: isMobileLayout ? "185px" : undefined,
		tableContentWidth: "100%",
		truncateString: true,
		cell: (row) => {
			return isRowCurrentlyEdited(row) && currentlyEditedRow ? (
				<CoreTextField
					placeholder={`${i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.enter")}${column.toLowerCase()}`}
					value={currentlyEditedRow.data[column]}
					onChange={(e) => {
						e.preventDefault();
						setCurrentlyEditedRow((prev) => ({ ...prev!, data: { ...prev!.data, [column]: e.target.value } }));
					}}
					sx={{
						width: "100%",
						"& .MuiInputBase-root": {
							width: "100%",
							height: "35px",
							marginRight: "0px",
							marginLeft: "0px",
							"& input": {
								textOverflow: "ellipsis",
							},
						},
					}}
					autoFocus={index === 0}
				/>
			) : (
				<Tooltip
					title={row.data[column] && <div style={{ fontSize: "13px", lineHeight: "160%" }}>{row.data[column]}</div>}
					placement="top-start"
					arrow
				>
					<div
						style={{
							color: "#575757",
							textAlign: "left",
							textOverflow: "ellipsis",
							cursor: "text",
							overflow: "hidden",
						}}
					>
						{row.data[column] || (
							<span style={{ color: "#575757" }}>
								{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.enter")}
								{column.toLowerCase()}
							</span>
						)}
					</div>
				</Tooltip>
			);
		},
		align: "left",
	}));

	const dropdownOptions = [
		{
			name: "Edit",
			onClick: (row: CoreTableRow<RowData>) => {
				setCurrentlyEditedRow(row);
			},
		},
		{
			name: "Delete",
			onClick: (row: CoreTableRow<RowData>) => {
				onRowsChange(rows.filter((r) => r !== row.data));
			},
		},
	];

	const ActionColumn = ({ row }: { row: CoreTableRow<RowData> }) => {
		const [menuShowing, setMenuShowing] = useState(false);

		const disableSaveIcon =
			isMobileLayout &&
			!!currentlyEditedRow &&
			(currentlyEditedRow.data["Email"] === undefined || currentlyEditedRow.data["Email"] === "");

		return (
			dropdownOptions.length > 0 &&
			(isRowCurrentlyEdited(row) ? (
				<div className={styles.editConfirmButtons}>
					<CoreIconButton
						tooltip={i18n.t(
							"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.ariaLabels.cancelIcon",
						)}
						onClick={() => {
							setCurrentlyEditedRow(null);
						}}
						variant="plain"
					>
						<CancelIcon style={{ color: "inherit" }} />
					</CoreIconButton>{" "}
					<CoreIconButton
						tooltip={
							disableSaveIcon
								? i18n.t(
										"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.saveIconDisabled",
									)
								: i18n.t(
										"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.ariaLabels.saveIcon",
									)
						}
						variant="plain"
						disabled={disableSaveIcon}
						onClick={() => {
							if (currentlyEditedRow) {
								onRowsChange([
									...rows.slice(0, currentlyEditedRow.absoluteIndex),
									{ ...currentlyEditedRow.data }, // update the row with the new data
									...rows.slice(currentlyEditedRow.absoluteIndex + 1),
								]);
							}
							setCurrentlyEditedRow(null);
						}}
					>
						<CheckMarkIcon style={{ color: "inherit" }} />
					</CoreIconButton>
				</div>
			) : (
				<div className={styles.menuButton}>
					<CoreMenuButton
						menuShowing={menuShowing}
						setMenuShowing={setMenuShowing}
						aria-label={i18n.t(
							"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.ariaLabels.actionsButton",
						)}
					>
						{dropdownOptions.map((item) => {
							if (item == null) {
								return null;
							}
							return (
								<CoreMenuItem
									key={item.name}
									onClick={() => {
										item.onClick(row);
										setMenuShowing(false);
									}}
								>
									<ListItemText primary={item.name} />
								</CoreMenuItem>
							);
						})}
					</CoreMenuButton>
				</div>
			))
		);
	};

	const tableColumnsWithAction: CoreTableColumn<RowData>[] = [
		...tableColumns,
		{
			key: "action",
			header: i18n.t("portal.components.assessmentPageTSX.columnList.action.header"),
			cell: (row) => <ActionColumn row={row} />,
			alignHeader: "center",
			width: "16%",
			tableCellContentWidth: "100%",
			sticky: isMobileLayout && currentlyEditedRow ? true : undefined,
		},
	];

	return (
		<>
			<div className={`${styles.boldText} ${styles.boldTextWrapper}`}>
				{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.manualSelect")}
			</div>
			{shouldShowAddTestTakerTable && rows.length > 0 && !isCSVParsed && (
				<CoreTable
					columns={tableColumnsWithAction}
					data={rows}
					rowsPerPage={5}
					stickyHeader={true}
					allowUIOverrides
					tableMargin={"0px 0px 20px 0px"}
					hover={rows.length > 1}
					unhoverableRows={currentlyEditedRow ? [currentlyEditedRow.absoluteIndex] : undefined}
				/>
			)}
			<Box>
				<CoreButton
					disabled={isCSVParsed}
					onClick={() => {
						if (!shouldShowAddTestTakerTable) {
							setShouldShowAddTestTakerTable(true);
						} else {
							const newRow = { Email: "", "First Name": "", "Last Name": "" };
							const updatedRows = rows.concat([newRow]);
							onRowsChange(updatedRows);
							setCurrentlyEditedRow({
								data: newRow,
								index: rows.length,
								absoluteIndex: rows.length,
							});
						}
					}}
				>
					{!shouldShowAddTestTakerTable
						? i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.addStu")
						: i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.addRow")}
				</CoreButton>
			</Box>

			<Box mt={2}>
				<CoreDividerWithText className={styles.boldText} />
			</Box>

			<div style={{ display: "inline-block" }}>
				<div style={{ fontWeight: 600, marginTop: "15px" }}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.bulkUpload")}
				</div>
			</div>

			<div className={styles.promptTextWrapper}>
				<div className={styles.promptText}>
					<span>
						<Trans
							i18nKey="portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.downloadCSVTemplate"
							components={{
								a: (
									<Link
										href="/stand-alone-assessment-template.csv"
										download="stand-alone-assessment-template.csv"
										sx={{ textDecoration: "none" }}
									/>
								),
							}}
							i18n={i18n}
						/>
					</span>
				</div>

				<CoreButton
					disabled={!isCSVParsed}
					color="cancel"
					onClick={() => {
						setCSVParsed(false);
						onRowsChange([]);
						coreToast.dismiss();
						coreToast.success(
							i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.csvRemovedToast"),
							{ autoClose: 2500 },
						);
					}}
					className={styles.removeCSVButton}
					sx={{
						// Override the disabled state to still get pointer events + cursor styling
						"&.Mui-disabled": {
							pointerEvents: "auto",
							cursor: "not-allowed",
						},
					}}
				>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.removeCSV")}
				</CoreButton>
			</div>

			{isCSVParsed ? (
				<div style={{ width: "100%" }}>
					<CoreTable
						columns={tableColumnsWithAction}
						data={rows}
						rowsPerPage={5}
						stickyHeader={true}
						allowUIOverrides
						tableMargin={"20px 0px 0px 0px"}
						hover={rows.length > 1}
						unhoverableRows={currentlyEditedRow ? [currentlyEditedRow.absoluteIndex] : undefined}
					/>
				</div>
			) : (
				<CoreUpload
					className={`${styles.csvBox} ${isMobileLayout ? styles.csvBoxCompact : ""}`}
					description={i18n.t(
						"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.dragAndDropCSV",
					)}
					buttonText={i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.browse")}
					fileTypes=".csv"
					onUpload={handleFileUpload}
				/>
			)}

			{csvParsingErrors !== null && (
				<CsvParsingErrorModal errors={csvParsingErrors} onClose={() => setCsvParsingErrors(null)} />
			)}
			{csvFileToBeParsedOnConfirm && (
				<CSVParseConfirmationModal
					onClose={() => setCSVFileToBeParsedOnConfirm(undefined)}
					csvFileToBeParsedOnConfirm={csvFileToBeParsedOnConfirm}
					onConfirm={parseCSVFile}
				/>
			)}
		</>
	);
}
